import { useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  resolveDocumentV3,
  type DocumentV3,
  type ResolvedBlock,
  type ResolvedPageV3,
} from '@docgen/schema';
import { paginatePage, type BlockBox, type Sheet } from '@/proposals/lib/paginate';
import {
  EditContext,
  SectionContext,
  type EditEvent,
  type RichEditorProps,
} from '@docgen/sdk';
import type { ComponentType } from 'react';
import { BLOCKS_BY_TYPE, BLOCKS_CSS, colorValue, pageAnchorId, scopeCss, styleToCss } from '@docgen/blocks';

/**
 * The document, rendered as real HTML in the app DOM — the same React render
 * serves the editor (editable slots, selection, hover) and the print route
 * (unscoped, full-bleed). No iframe, no bridge: every interaction is native.
 */

export const DOC_SCOPE = 'dgdoc';

/* NOTE: data-edit sits ON the scope root, so edit-chrome rules must be
   written :root[data-edit] — scopeCss compounds :root onto the scope class. */
const EDIT_CSS = `
body{background:transparent}
:root[data-edit] [data-block]{cursor:default}
:root[data-edit] [data-hovered]{outline:1.5px solid #93b4f5;outline-offset:1px}
:root[data-edit] [data-selected]{outline:2px solid #1851d1;outline-offset:1px}
:root[data-edit] [data-slot][contenteditable]{cursor:text}
:root[data-edit] [data-slot][contenteditable]:focus{outline:2px solid #1851d1;border-radius:2px}
:root[data-edit] [data-slot] .ProseMirror{outline:none;cursor:text}
:root[data-edit] [data-slot]:has(.ProseMirror-focused){outline:2px solid #1851d1;border-radius:2px}
:root[data-edit] .dg-item{position:relative}
.dg-item-controls{position:absolute;top:2px;right:0;display:none;gap:2px;z-index:5}
:root[data-edit] .dg-item:hover>.dg-item-controls{display:inline-flex}
.dg-item-controls button{width:17px;height:17px;border-radius:4px;border:1px solid var(--line);background:#fff;color:var(--blue);font-size:12px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;font-family:inherit}
.dg-item-controls button:hover{color:var(--royal);border-color:var(--royal)}
.page{box-shadow:0 3px 18px rgba(9,20,49,.3)}
`;

function themeVarsCss(doc: DocumentV3): string {
  // `?? {}`: a stored theme can arrive without colors (the database drops an
  // empty object); render with no tokens rather than crash the page.
  const vars = Object.entries(doc.theme.colors ?? {})
    .map(([token, value]) => `--${token}:${value};`)
    .join('');
  return `:root{${vars}}`;
}

// The first block on a sheet starts flush at the top padding: its leading
// margin belongs to the previous sheet's break, not the top of this one. Zeroing
// it keeps the rendered sheet height in step with what the paginator measured —
// needed on BOTH the print sheets and the paginated editor cards.
const SHEET_FIRST_CHILD_CSS = `.page>[data-key]:first-child{margin-top:0}`;

export function documentCss(doc: DocumentV3, opts: { print: boolean }): string {
  const raw = [
    themeVarsCss(doc),
    BLOCKS_CSS,
    doc.theme.baseCss,
    SHEET_FIRST_CHILD_CSS,
    opts.print ? '' : EDIT_CSS,
  ]
    .filter(Boolean)
    .join('\n');
  return opts.print ? raw : scopeCss(raw, `.${DOC_SCOPE}`);
}

export interface DocumentViewProps {
  doc: DocumentV3;
  editable?: boolean;
  selectedId?: string | null;
  hoveredId?: string | null;
  onSelect?: (blockId: string | null, blockKey?: string) => void;
  onHover?: (blockId: string | null) => void;
  onSlotEdit?: (event: EditEvent) => void;
  /** rich-text editing surface injected into RichSlot (e.g. TipTap) */
  richEditor?: ComponentType<RichEditorProps>;
  /** full-bleed unscoped render for the PDF pass (paginates over-tall pages) */
  print?: boolean;
  /** called once the print pass has measured and paginated (gates print-ready) */
  onReady?: () => void;
}

interface ViewCallbacks {
  editable: boolean;
  selectedId: string | null;
  hoveredId: string | null;
  onSelect: (blockId: string | null, blockKey?: string) => void;
  onHover: (blockId: string | null) => void;
}

function BlockView({
  block,
  view,
  itemOffset,
}: {
  block: ResolvedBlock;
  view: ViewCallbacks;
  itemOffset?: number;
}) {
  const def = BLOCKS_BY_TYPE.get(block.type);
  const scope = useMemo(
    () => ({
      sectionKey: block.key,
      sectionId: block.blockId,
      props: block.props,
      ...(itemOffset ? { itemOffset } : {}),
    }),
    [block, itemOffset],
  );
  if (!def) {
    return (
      <div data-block={block.blockId} data-key={block.key} style={{ color: '#b3261e', fontSize: '9pt' }}>
        Unknown block type: {block.type}
      </div>
    );
  }
  const attrs: Record<string, unknown> = {
    'data-block': block.blockId,
    'data-key': block.key,
    ...(def.acceptsChildren ? { 'data-container': '' } : {}),
    ...(view.selectedId === block.blockId ? { 'data-selected': '' } : {}),
    ...(view.hoveredId === block.blockId ? { 'data-hovered': '' } : {}),
  };
  if (view.editable) {
    attrs['onClick'] = (e: MouseEvent) => {
      e.stopPropagation();
      view.onSelect(block.blockId, block.key);
    };
    attrs['onMouseOver'] = (e: MouseEvent) => {
      e.stopPropagation();
      view.onHover(block.blockId);
    };
    attrs['onMouseOut'] = (e: MouseEvent) => {
      e.stopPropagation();
      view.onHover(null);
    };
  }
  const children = block.children.length
    ? block.children.map((child) => <BlockView key={child.key} block={child} view={view} />)
    : undefined;
  const Component = def.Component;
  return (
    <SectionContext.Provider value={scope}>
      <Component attrs={attrs} style={styleToCss(block.style)}>
        {children}
      </Component>
    </SectionContext.Provider>
  );
}

function PageView({ page, view }: { page: ResolvedPageV3; view: ViewCallbacks }) {
  const background = colorValue(page.background);
  return (
    <section
      className="page"
      id={pageAnchorId(page.key)}
      data-page={page.pageId}
      data-page-key={page.key}
      style={{
        ...(background !== undefined ? { background } : {}),
        ...(page.pad !== undefined ? { padding: page.pad } : {}),
      }}
    >
      {page.blocks.map((block) => (
        <BlockView key={block.key} block={block} view={view} />
      ))}
    </section>
  );
}

// ---- Print pagination -------------------------------------------------------
// A logical page is one authored section; if its content is taller than a
// physical sheet it must flow onto more sheets instead of being clipped. We
// render every page once, hidden, measure each block (and each item of a
// splittable block), pack them into sheets with the pure paginator, then render
// the sheets. Screen editing keeps the one-card-per-page view (Phase 2).

const SHEET_HEIGHT_PX = 11 * 96; // Letter at 96dpi
// Pack a hair conservatively so sub-pixel rounding and tiny screen↔print
// rendering differences never tip a sheet's content past the fixed sheet height
// (which would clip). Small, because the measuring layer now matches the real
// flex render (min-height:0 keeps margins non-collapsing without inflation).
const FIT_SAFETY_PX = 8;
// How far the page footer may dip into the bottom padding (0.72in = 69px), so
// a page a few px over keeps its footer rather than spilling onto a new sheet.
const FOOTER_ALLOWANCE_PX = 24;
/** The block type of the footer pinned to each page's bottom. */
const PAGE_FOOTER_TYPE = 'pageFooter';

/** The inline style PageView / a sheet applies (background + padding override). */
function pageStyle(page: ResolvedPageV3): CSSProperties {
  const background = colorValue(page.background);
  return {
    ...(background !== undefined ? { background } : {}),
    ...(page.pad !== undefined ? { padding: page.pad } : {}),
  };
}

/** A packed sheet as rendered: the paginator's sheet, plus a trimmed bottom padding when the footer needed it. */
type RenderSheet = Sheet & { readonly paddingBottomPx?: number };

/** The flex filler that pins a footer to the sheet bottom. */
function isGrowBlock(block: ResolvedBlock): boolean {
  return block.type === 'spacer' && (block.style as { grow?: unknown } | undefined)?.grow === true;
}

// A splittable block re-rendered with only items [from, to). The KEY is
// preserved (edits route by blockId + key), so the slice offset — not a mangled
// key — is how item slots reach the real data indices.
function sliceBlock(block: ResolvedBlock, from: number, to: number): ResolvedBlock {
  const props: Record<string, unknown> = { ...(block.props as Record<string, unknown>) };
  for (const name of ['items', 'rows']) {
    const arr = props[name];
    if (Array.isArray(arr)) props[name] = arr.slice(from, to);
  }
  return { ...block, props };
}

/** How many CSS columns an element lays its children into (1 when none). */
function cssColumnCount(el: HTMLElement): number {
  const count = Number.parseInt(getComputedStyle(el).columnCount, 10);
  return Number.isFinite(count) && count > 1 ? count : 1;
}

/** An element's height plus its vertical margins. */
function outerHeight(el: HTMLElement): number {
  const cs = getComputedStyle(el);
  return el.getBoundingClientRect().height + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
}

/** Measure a hidden page section into paginator boxes at its real content width. */
function measureSection(
  section: HTMLElement,
  blocks: readonly ResolvedBlock[],
): { contentHeight: number; padBottom: number; boxes: BlockBox[] } {
  const cs = getComputedStyle(section);
  const padTop = parseFloat(cs.paddingTop) || 0;
  const padBot = parseFloat(cs.paddingBottom) || 0;
  const contentHeight = Math.max(1, SHEET_HEIGHT_PX - padTop - padBot - FIT_SAFETY_PX);
  const roots = Array.from(section.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement && el.hasAttribute('data-key'),
  );
  const boxes: BlockBox[] = [];
  // Fall back to one sheet if the DOM and model don't line up 1:1.
  if (roots.length !== blocks.length) return { contentHeight, padBottom: padBot, boxes };
  for (let i = 0; i < roots.length; i++) {
    const el = roots[i]!;
    const block = blocks[i]!;
    const rect = el.getBoundingClientRect();
    const top = rect.top;
    const nextTop = i + 1 < roots.length ? roots[i + 1]!.getBoundingClientRect().top : rect.bottom;
    const height = Math.max(0, nextTop - top); // includes the gap to the next block
    const def = BLOCKS_BY_TYPE.get(block.type);
    const base: BlockBox = { key: block.key, height, split: 'none' };
    if (isGrowBlock(block)) {
      boxes.push({ ...base, isGrow: true });
      continue;
    }
    if (def?.split === 'items') {
      const items = Array.from(el.querySelectorAll<HTMLElement>(':scope > .dg-item'));
      if (items.length > 0) {
        const firstTop = items[0]!.getBoundingClientRect().top;
        const overhead = Math.max(0, firstTop - top);
        const columns = cssColumnCount(el);
        // In a single column, an item's share is the distance to the next one
        // (its gap included). Across CSS columns that distance jumps back up
        // at each column break, so each item is measured on its own instead and
        // the paginator balances them across the columns.
        const itemHeights =
          columns > 1
            ? items.map(outerHeight)
            : items.map((it, j) => {
                const t = it.getBoundingClientRect().top;
                const nt = j + 1 < items.length ? items[j + 1]!.getBoundingClientRect().top : rect.bottom;
                return Math.max(0, nt - t);
              });
        boxes.push({
          key: block.key,
          height,
          split: 'items',
          overhead,
          itemHeights,
          ...(columns > 1 ? { columns } : {}),
          keepWithNext: def.keepWithNext,
        });
        continue;
      }
    }
    boxes.push({ ...base, keepWithNext: def?.keepWithNext, ...(block.type === PAGE_FOOTER_TYPE ? { isFooter: true } : {}) });
  }
  return { contentHeight, padBottom: padBot, boxes };
}

/** One logical page rendered as its computed sheets. */
function SheetsView({ page, sheets, view }: { page: ResolvedPageV3; sheets: RenderSheet[]; view: ViewCallbacks }) {
  const byKey = new Map(page.blocks.map((b) => [b.key, b] as const));
  const hasGrow = page.blocks.some(isGrowBlock);
  return (
    <>
      {sheets.map((sheet, si) => {
        const nodes = sheet.placements.flatMap((pl) => {
          const block = byKey.get(pl.key);
          if (!block) return [];
          if (pl.kind === 'slice') {
            return [
              <BlockView
                key={`${pl.key}~${pl.from}`}
                block={sliceBlock(block, pl.from, pl.to)}
                view={view}
                itemOffset={pl.from}
              />,
            ];
          }
          return [<BlockView key={pl.key} block={block} view={view} />];
        });
        // On the last sheet, re-add the grow filler so the footer pins to the bottom.
        if (si === sheets.length - 1 && hasGrow) {
          const footerAt = sheet.placements.findIndex((pl) => byKey.get(pl.key)?.type === 'pageFooter');
          const filler = <div key="dg-grow" aria-hidden style={{ flexGrow: 1 }} />;
          if (footerAt >= 0) nodes.splice(footerAt, 0, filler);
          else nodes.push(filler);
        }
        return (
          <section
            key={`${page.key}#${si}`}
            className="page"
            {...(si === 0 ? { id: pageAnchorId(page.key) } : {})}
            data-page={page.pageId}
            data-page-key={page.key}
            data-sheet={si}
            style={{
              ...pageStyle(page),
              ...(sheet.paddingBottomPx !== undefined ? { paddingBottom: `${sheet.paddingBottomPx}px` } : {}),
            }}
          >
            {nodes}
          </section>
        );
      })}
    </>
  );
}

/** Non-interactive view + edit state for the hidden measuring layer. */
const MEASURE_VIEW: ViewCallbacks = {
  editable: false,
  selectedId: null,
  hoveredId: null,
  onSelect: () => {},
  onHover: () => {},
};
const MEASURE_EDIT_STATE = { editMode: false, selectedId: null, hoveredId: null, emit: () => {} };
/** Class on the hidden measuring wrapper; scopes its top-packed-flow override. */
const MEASURE_CLASS = 'dg-measure';

function singleSheet(page: ResolvedPageV3): RenderSheet[] {
  return [{ placements: page.blocks.map((b) => ({ kind: 'block', key: b.key })) }];
}

/**
 * Measures every page and renders the resulting sheets. The measuring copy is
 * portalled to <body> — never inside the editor's paper — so its duplicate
 * blocks can't collide with the editor's DOM queries; it is read-only so it
 * carries no contenteditable or handlers. Re-measures when the document changes
 * WITHOUT blanking the current sheets (no edit-time flicker).
 */
function PaginatedDocument({
  pages,
  view,
  css,
  scoped,
  onReady,
}: {
  pages: readonly ResolvedPageV3[];
  view: ViewCallbacks;
  css: string;
  scoped: boolean;
  onReady?: (() => void) | undefined;
}) {
  const measureRef = useRef<HTMLDivElement>(null);
  const [sheetsByKey, setSheetsByKey] = useState<Map<string, RenderSheet[]> | null>(null);
  // Extra per-page headroom, added when a rendered sheet is found to overflow.
  // Measurement can drift a few px on some blocks; rather than a large global
  // margin (which splits near-full pages like the TOC eagerly), we pack tight
  // and let the verify pass below bump only the pages that actually overflow.
  const [extraSafety, setExtraSafety] = useState<ReadonlyMap<string, number>>(new Map());

  // Per-page headroom is re-derived for the CURRENT content: clear it when the
  // document changes so a page edited shorter isn't held split by a stale bump.
  // (Guarded so the common no-drift case doesn't trigger an extra pass.)
  useEffect(() => {
    setExtraSafety((prev) => (prev.size > 0 ? new Map() : prev));
  }, [pages]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const measure = (): void => {
      const root = measureRef.current;
      if (cancelled || !root) return;
      const map = new Map<string, RenderSheet[]>();
      for (const page of pages) {
        const section = root.querySelector<HTMLElement>(`[data-measure-page="${CSS.escape(page.key)}"]`);
        if (!section) {
          map.set(page.key, singleSheet(page));
          continue;
        }
        const { contentHeight, padBottom, boxes } = measureSection(section, page.blocks);
        const budget = contentHeight - (extraSafety.get(page.key) ?? 0);
        const sheets = boxes.length > 0 ? paginatePage(boxes, budget, { footerAllowance: FOOTER_ALLOWANCE_PX }) : singleSheet(page);
        // A footer that dipped into the bottom padding: that sheet's padding shrinks to match.
        map.set(
          page.key,
          sheets.map((sheet) =>
            sheet.bottomTrim ? { ...sheet, paddingBottomPx: Math.max(0, padBottom - sheet.bottomTrim) } : sheet,
          ),
        );
      }
      setSheetsByKey(map);
    };

    // Measure only once BOTH fonts and images have settled: block height is set
    // by CSS max-height (`height:300px`), so an unloaded image measures ~0 and
    // the page would under-split. A 3s cap keeps a broken/slow asset from hanging
    // the render (and thus the headless PDF, which waits on onReady).
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready;
    const settled = Promise.resolve(fonts)
      .catch(() => undefined)
      .then(() => {
        if (cancelled) return;
        const root = measureRef.current;
        const imgs = root ? Array.from(root.querySelectorAll('img')) : [];
        const loads = imgs
          .filter((img) => !img.complete)
          .map(
            (img) =>
              new Promise<void>((res) => {
                img.addEventListener('load', () => res(), { once: true });
                img.addEventListener('error', () => res(), { once: true });
              }),
          );
        return Promise.race([
          Promise.all(loads).then(() => undefined),
          new Promise<void>((res) => {
            timer = setTimeout(res, 3000);
          }),
        ]);
      });
    void settled.then(() => {
      // A macrotask so React has committed and layout is flushed by getBoundingClientRect.
      timer = setTimeout(measure, 0);
    });

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [pages, extraSafety]);

  // Verify the RENDERED sheets: if any page's tallest sheet overflowed the
  // physical sheet, give that page more headroom and re-paginate just it. Only
  // ever grows, is capped, and converges in a pass or two — so a page whose
  // measurement drifts a little still never clips.
  useEffect(() => {
    if (!sheetsByKey) return;
    const maxByKey = new Map<string, number>();
    for (const el of document.querySelectorAll<HTMLElement>('section.page[data-page-key]')) {
      const k = el.getAttribute('data-page-key');
      if (!k) continue;
      maxByKey.set(k, Math.max(maxByKey.get(k) ?? 0, el.getBoundingClientRect().height));
    }
    let changed = false;
    const next = new Map(extraSafety);
    for (const [k, h] of maxByKey) {
      const over = h - SHEET_HEIGHT_PX;
      const cur = extraSafety.get(k) ?? 0;
      if (over > 1 && cur < 160) {
        next.set(k, Math.min(160, cur + over + 4));
        changed = true;
      }
    }
    if (changed) setExtraSafety(next);
    // Converged (nothing overflows, or every overflow is capped): the layout is
    // final. Gate print-ready on this so the PDF never captures a pre-repack pass.
    else onReady?.();
  }, [sheetsByKey, extraSafety, onReady]);

  const measuringLayer = createPortal(
    <div
      className={scoped ? `${DOC_SCOPE} ${MEASURE_CLASS}` : MEASURE_CLASS}
      aria-hidden
      style={{ position: 'absolute', left: -99999, top: 0, visibility: 'hidden', pointerEvents: 'none' }}
    >
      <style>{css}</style>
      {/* Measure at the page's NATURAL height. The real .page is a flex column
          with min-height:11in, so `grow` fillers and `margin-top:auto` bands
          (the TOC's "at a glance") absorb the free space and get pushed to the
          bottom, inflating measured gaps and splitting a page that fits. Setting
          min-height:0 removes that free space so they collapse — while KEEPING
          flex (block layout would collapse real margins and under-measure). */}
      <style>{`.${MEASURE_CLASS} .page{min-height:0 !important}`}</style>
      <EditContext.Provider value={MEASURE_EDIT_STATE}>
        <div ref={measureRef}>
          {pages.map((page) => (
            <section key={page.key} className="page" data-measure-page={page.key} style={pageStyle(page)}>
              {page.blocks.map((block) => (
                <BlockView key={block.key} block={block} view={MEASURE_VIEW} />
              ))}
            </section>
          ))}
        </div>
      </EditContext.Provider>
    </div>,
    document.body,
  );

  return (
    <>
      {measuringLayer}
      {sheetsByKey
        ? pages.map((page) => (
            <SheetsView key={page.key} page={page} sheets={sheetsByKey.get(page.key) ?? singleSheet(page)} view={view} />
          ))
        : // Until the first measurement lands, show the pages unpaginated rather
          // than a blank canvas; they swap to sheets a frame later.
          pages.map((page) => <PageView key={page.key} page={page} view={view} />)}
    </>
  );
}

export function DocumentView({
  doc,
  editable = false,
  selectedId = null,
  hoveredId = null,
  onSelect,
  onHover,
  onSlotEdit,
  richEditor,
  print = false,
  onReady,
}: DocumentViewProps) {
  const resolved = useMemo(() => resolveDocumentV3(doc), [doc]);
  const css = useMemo(() => documentCss(doc, { print }), [doc, print]);

  const editState = useMemo(
    () => ({
      editMode: editable,
      selectedId,
      hoveredId,
      emit: (event: EditEvent) => onSlotEdit?.(event),
      richEditor,
    }),
    [editable, selectedId, hoveredId, onSlotEdit, richEditor],
  );

  const view: ViewCallbacks = {
    editable,
    selectedId,
    hoveredId,
    onSelect: (blockId, blockKey) => onSelect?.(blockId, blockKey),
    onHover: (blockId) => onHover?.(blockId),
  };

  return (
    <EditContext.Provider value={editState}>
      <style>{css}</style>
      <div
        className={print ? undefined : DOC_SCOPE}
        {...(editable ? { 'data-edit': '' } : {})}
        onClick={editable ? () => view.onSelect(null) : undefined}
      >
        {print || editable ? (
          <PaginatedDocument
            pages={resolved.pages}
            view={view}
            css={css}
            scoped={!print}
            onReady={onReady}
          />
        ) : (
          resolved.pages.map((page) => <PageView key={page.key} page={page} view={view} />)
        )}
      </div>
    </EditContext.Provider>
  );
}
